import { useIntl } from "react-intl";
import { DataTableStringColumn } from "~components/DataTable/components/DataTableStringColumn/DataTableStringColumn.tsx";
import type { DataTableColumn } from "~components/DataTable/types/dataTableColumn.ts";
import type { DataTableRow } from "~components/DataTable/types/dataTableRow.ts";
import { useStandingsConfigurationService } from "~pages/StandingsConfigurations/hooks/useStandingsConfigurationService.ts";
import { StandingsConfigurationTableColumns } from "~pages/StandingsConfigurations/StandingsConfigurationListPage/enums/standingsConfigurationTableColumns.ts";
import type { UpdateStandingsConfiguration } from "~pages/StandingsConfigurations/types/upsertStandingsConfiguration.ts";
import type { StandingConfiguration } from "~types/standingConfiguration.ts";

export const useGetStandingsConfigurationTableColumns = (massEditMode: boolean): Array<DataTableColumn<StandingConfiguration>> => {
  const { formatMessage } = useIntl();
  const { updateStandingsConfiguration } = useStandingsConfigurationService();

  const updateStandingsConfigurationField = async (
    row: StandingConfiguration & DataTableRow,
    updateData: Omit<UpdateStandingsConfiguration, "standingConfigurationId">,
  ) => {
    row.disabled = true;
    try {
      await updateStandingsConfiguration(row.standingConfigurationId, updateData);
    } finally {
      row.disabled = false;
    }
  };

  return [
    {
      key: StandingsConfigurationTableColumns.NameLocal,
      title: formatMessage({ id: "name.local.full" }),
      dataIndex: "nameLocal",
      width: 280,
      ellipsis: { showTitle: true },
      render: (value: string, row) => (
        <DataTableStringColumn
          value={value}
          isEditMode={massEditMode}
          disabled={row.disabled}
          required
          onSave={(nameLocal) => updateStandingsConfigurationField(row, { nameLocal })}
        />
      ),
    },
    {
      key: StandingsConfigurationTableColumns.NameLatin,
      title: formatMessage({ id: "name.latin.full" }),
      dataIndex: "nameLatin",
      width: 280,
      ellipsis: { showTitle: true },
      render: (value: string | null, row) => (
        <DataTableStringColumn
          value={value}
          isEditMode={massEditMode}
          disabled={row.disabled}
          onSave={(nameLatin) => updateStandingsConfigurationField(row, { nameLatin: nameLatin || null })}
        />
      ),
    },
    {
      key: StandingsConfigurationTableColumns.StandingConfigurationId,
      title: formatMessage({ id: "standings.configuration.id" }),
      dataIndex: "standingConfigurationId",
      copyable: true,
      width: 220,
    },
    {
      key: StandingsConfigurationTableColumns.ExternalId,
      title: formatMessage({ id: "externalId" }),
      dataIndex: "externalId",
      copyable: true,
      width: 220,
      render: (value: string | null, row) => (
        <DataTableStringColumn
          value={value}
          isEditMode={massEditMode}
          disabled={row.disabled}
          onSave={(externalId) => updateStandingsConfigurationField(row, { externalId: externalId || null })}
        />
      ),
    },
  ];
};
